import { useEffect, useState } from "react";
import { ArrowLeft, FileText, Download, User, Plus, Trash2, BellRing, Loader2, CheckCircle2, Circle, Gauge, PawPrint } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { SectionTitle, Card } from "../ui";

export default function ClientsManager() {
  const [rows, setRows] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/admin/clients").then((r) => setRows(r.data)); }, []);

  if (active) return <ClientDocs client={active} onBack={() => setActive(null)} />;
  if (!rows) return <p className="text-charcoal-500">Loading…</p>;

  const readinessOf = (c) => {
    const checks = [];
    if (c.docs_total > 0) checks.push(c.docs_done === c.docs_total);
    checks.push(!!c.has_booking);
    checks.push(!!c.quote_accepted);
    return Math.round((100 * checks.filter(Boolean).length) / checks.length);
  };
  const badgeStyle = (s) => s >= 100 ? "bg-sage/15 text-sage" : s >= 50 ? "bg-terracotta-light text-terracotta-700" : "bg-sand-200 text-charcoal-500";

  return (
    <div data-testid="admin-clients">
      <SectionTitle title="Clients" subtitle="Registered client accounts, their quotes, and uploaded documents." />
      {rows.length === 0 ? (
        <Card><p className="text-charcoal-500">No client accounts yet. Clients register via the “Client login” link on the site.</p></Card>
      ) : (
        <div className="space-y-3">
          {rows.map((c) => {
            const score = readinessOf(c);
            return (
            <button key={c.id} data-testid={`client-row-${c.id}`} onClick={() => setActive(c)}
              className="w-full text-left rounded-xl border border-sand-200 bg-white p-4 hover:border-terracotta transition-colors flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sand-100 text-charcoal-500"><User size={16} /></span>
                <div>
                  <div className="font-medium text-charcoal-800">{c.name || "Unnamed"}</div>
                  <div className="text-xs text-charcoal-500">{c.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-xs text-charcoal-500">{c.quotes} quote(s) · {c.documents} doc(s)</div>
                <span data-testid={`client-readiness-badge-${c.id}`} title="Move readiness"
                  className={`rounded-full px-2.5 py-1 text-xs font-medium ${badgeStyle(score)}`}>
                  {score}%
                </span>
              </div>
            </button>
          );})}
        </div>
      )}
    </div>
  );
}

function ClientDocs({ client, onBack }) {
  const [docs, setDocs] = useState(null);
  useEffect(() => { api.get(`/admin/clients/${client.id}/documents`).then((r) => setDocs(r.data)); }, [client.id]);

  const download = async (d) => {
    try {
      const res = await api.get(`/admin/documents/${d.id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not open the document."); }
  };

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-terracotta mb-4"><ArrowLeft size={15} /> Back to clients</button>
      <SectionTitle title={client.name || client.email} subtitle={client.email} />
      <FamilyPetsCard client={client} />
      <ReadinessCard clientId={client.id} />
      <VideosWatchedCard clientId={client.id} />
      <DocRequestsCard clientId={client.id} />
      <Card>
        <h3 className="font-medium text-charcoal-800 mb-3">Uploaded documents</h3>
        {!docs ? <p className="text-charcoal-500">Loading…</p> : docs.length === 0 ? (
          <p className="text-sm text-charcoal-500">This client hasn't uploaded any documents yet.</p>
        ) : (
          <div className="divide-y divide-sand-100">
            {docs.map((d) => (
              <div key={d.id} data-testid={`admin-doc-${d.id}`} className="flex items-center justify-between py-2.5">
                <div className="flex items-center gap-2 text-sm text-charcoal-700"><FileText size={15} className="text-charcoal-400" /> {d.original_filename}</div>
                <button onClick={() => download(d)} className="inline-flex items-center gap-1 text-sm text-terracotta hover:underline"><Download size={14} /> Open</button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const REQ_STATUS_STYLE = { pending: "bg-sand-200 text-charcoal-600", received: "bg-sage/15 text-sage", waived: "bg-sand-100 text-charcoal-400" };

const PET_DETAIL_KEYS = [
  ["breed", "Breed"], ["sex", "Sex"], ["size", "Size"], ["weight", "Weight"], ["age", "Age"],
  ["energy_level", "Energy"], ["vaccination_status", "Vaccines"], ["medical_needs", "Medical"],
  ["favorite_treat", "Favorite treat/toy"], ["temperament", "Temperament"],
];

function FamilyPetsCard({ client }) {
  const pets = client.pets || [];
  const hasProfile = client.preferred_name || client.party_adults != null || client.personalization_notes || pets.length;
  if (!hasProfile) return null;
  return (
    <Card className="mb-6">
      <h3 className="flex items-center gap-2 font-medium text-charcoal-800 mb-3" data-testid="admin-family-pets-card">
        <PawPrint size={16} className="text-terracotta" /> Family &amp; pets — know before you call
      </h3>
      <div className="grid gap-2 text-sm text-charcoal-700 sm:grid-cols-3 mb-3">
        <p><span className="text-charcoal-400">Call them:</span> <span className="font-medium">{client.preferred_name || client.name || "—"}</span></p>
        <p><span className="text-charcoal-400">Traveling:</span> {client.party_adults != null || client.party_kids != null
          ? `${client.party_adults ?? 0} adult(s), ${client.party_kids ?? 0} kid(s)` : "—"}</p>
        <p><span className="text-charcoal-400">Pets:</span> {pets.length || "None"}</p>
      </div>
      {client.personalization_notes && (
        <p className="mb-3 rounded-lg bg-sand-100 px-3 py-2 text-sm text-charcoal-700">
          <span className="text-charcoal-400">Comfort notes:</span> "{client.personalization_notes}"
        </p>
      )}
      {pets.map((p, i) => (
        <div key={p.id || i} data-testid={`admin-pet-card-${i}`} className="mb-2 rounded-xl border border-sand-200 bg-sand-100/60 p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-charcoal-800">
            {p.photo ? <img src={p.photo} alt="" className="h-8 w-8 rounded-full object-cover" /> : <PawPrint size={14} className="text-terracotta" />}
            {p.name || "Unnamed"} <span className="text-charcoal-400 font-normal capitalize">· {p.species || "pet"}</span>
            {p.crate_trained && <span className="rounded-full bg-sage/15 px-2 py-0.5 text-[11px] text-sage">crate-trained</span>}
            {p.spayed_neutered && <span className="rounded-full bg-sand-200 px-2 py-0.5 text-[11px] text-charcoal-600">spayed/neutered</span>}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-charcoal-600">
            {PET_DETAIL_KEYS.filter(([k]) => p[k]).map(([k, label]) => (
              <span key={k}><span className="text-charcoal-400">{label}:</span> {p[k]}</span>
            ))}
          </div>
        </div>
      ))}
    </Card>
  );
}

function VideosWatchedCard({ clientId }) {
  const [views, setViews] = useState(null);
  useEffect(() => {
    api.get(`/admin/clients/${clientId}/video-views`).then((r) => setViews(r.data)).catch(() => setViews([]));
  }, [clientId]);
  if (!views || views.length === 0) return null;
  return (
    <Card className="mb-6">
      <h3 className="font-medium text-charcoal-800 mb-3" data-testid="client-videos-watched">
        Videos watched — talking points for the next call
      </h3>
      <div className="divide-y divide-sand-100">
        {views.map((v) => (
          <div key={v.id} className="flex items-center justify-between py-2 text-sm">
            <a href={`/life-in-mexico/${v.video_slug}`} target="_blank" rel="noreferrer" className="text-charcoal-700 hover:text-terracotta">
              {v.video_title || v.video_slug}
            </a>
            <span className="text-xs text-charcoal-400">{v.views || 1}× · last {v.last_viewed ? new Date(v.last_viewed).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ReadinessCard({ clientId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/clients/${clientId}/readiness`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [clientId]);

  if (!data) return null;
  const color = data.score >= 100 ? "text-sage" : data.score >= 50 ? "text-terracotta" : "text-clay-800";

  return (
    <Card className="mb-6">
      <div className="flex flex-wrap items-center gap-6" data-testid="client-readiness-card">
        <div className="flex items-center gap-3">
          <Gauge size={22} className={color} strokeWidth={1.5} />
          <div>
            <div className={`font-serif text-3xl ${color}`} data-testid="readiness-score">{data.score}%</div>
            <div className="text-xs text-charcoal-500">Move readiness</div>
          </div>
        </div>
        <div className="flex-1 min-w-[240px] space-y-1.5">
          {data.checks.map((c) => (
            <div key={c.key} data-testid={`readiness-check-${c.key}`} className="flex items-center gap-2 text-sm">
              {c.done
                ? <CheckCircle2 size={15} className="text-sage shrink-0" />
                : <Circle size={15} className={`shrink-0 ${c.applicable ? "text-charcoal-300" : "text-sand-300"}`} />}
              <span className={c.done ? "text-charcoal-800" : "text-charcoal-500"}>{c.label}</span>
              <span className="text-xs text-charcoal-400">· {c.detail}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function DocRequestsCard({ clientId }) {
  const [reqs, setReqs] = useState(null);
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/clients/${clientId}/document-requests`).then((r) => setReqs(r.data));
  useEffect(() => { load().catch(() => setReqs([])); }, [clientId]);

  const add = async () => {
    if (!label.trim()) return toast.error("Enter a document name.");
    setBusy(true);
    try {
      await api.post(`/admin/clients/${clientId}/document-requests`, { items: [{ label, note }] });
      toast.success("Requested — the client was emailed.");
      setLabel(""); setNote("");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    setBusy(false);
  };

  const setStatus = async (r, status) => {
    try { await api.patch(`/admin/document-requests/${r.id}`, { status }); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove the request "${r.label}"?`)) return;
    try { await api.delete(`/admin/document-requests/${r.id}`); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const remind = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/clients/${clientId}/document-requests/remind`);
      toast.success(`Reminder sent (${res.data.reminded} pending item(s)).`);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    setBusy(false);
  };

  const pendingCount = (reqs || []).filter((r) => r.status === "pending").length;

  return (
    <Card className="mb-6">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-medium text-charcoal-800">Documents we need from this client</h3>
        {pendingCount > 0 && (
          <button data-testid="doc-req-remind-btn" onClick={remind} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 px-4 py-1.5 text-xs font-medium text-charcoal-700 hover:bg-sand-100 disabled:opacity-60">
            <BellRing size={13} /> Send reminder ({pendingCount} pending)
          </button>
        )}
      </div>
      {!reqs ? <p className="text-charcoal-500">Loading…</p> : (
        <>
          {reqs.length === 0 && <p className="text-sm text-charcoal-500 mb-3">Nothing requested yet.</p>}
          {reqs.length > 0 && (
            <div className="divide-y divide-sand-100 mb-4">
              {reqs.map((r) => (
                <div key={r.id} data-testid={`admin-doc-req-${r.id}`} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-charcoal-800">{r.label}</div>
                    {r.note && <div className="text-xs text-charcoal-500">{r.note}</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${REQ_STATUS_STYLE[r.status] || ""}`}>{r.status}</span>
                    <select value={r.status} onChange={(e) => setStatus(r, e.target.value)} data-testid={`doc-req-status-${r.id}`}
                      className="rounded-lg border border-sand-300 bg-white px-2 py-1 text-xs text-charcoal-700">
                      <option value="pending">pending</option>
                      <option value="received">received</option>
                      <option value="waived">waived</option>
                    </select>
                    <button onClick={() => remove(r)} data-testid={`doc-req-delete-${r.id}`} aria-label={`Remove ${r.label}`}
                      className="p-1 text-charcoal-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Document name (e.g. Passport copy)"
              data-testid="doc-req-label-input" className="w-56 rounded-lg border border-sand-300 px-3 py-2 text-sm" />
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the client (optional)"
              data-testid="doc-req-note-input" className="flex-1 min-w-[180px] rounded-lg border border-sand-300 px-3 py-2 text-sm" />
            <button onClick={add} disabled={busy} data-testid="doc-req-add-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-4 py-2 text-sm text-white font-medium hover:bg-terracotta-hover disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Request
            </button>
          </div>
          <p className="text-xs text-charcoal-400 mt-2">The client is emailed a checklist and can upload each item from their portal. Uploads mark items received automatically.</p>
        </>
      )}
    </Card>
  );
}
